// File: frontend/src/components/Navbar.js
// Version: 3.1 (Wave Translation — Design System & i18n)
// Date: 2026-10-22

import React, { useState, useRef, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';
import { useUiLanguage } from '../context/UiLanguageContext';
import { getUserRoleLabel, getRoleColor, isAllowedByKey } from '../config/navPermissions';
import { legacyGroups, fitenkoRail } from '../config/fitenkoNav';
import FitenkoRail, { FitenkoRailHead, FitenkoRailUser } from './FitenkoRail';
import FitenkoVerhnyayaPanel from './FitenkoVerhnyayaPanel';
import { canSeeKatifi, aiName } from '../config/katifiAccess';
import { useNavCounters } from '../hooks/useNavCounters';
import './Navbar.css';
import UiLanguageToggle from './UiLanguageToggle';

// Dropdown component
const NavDropdown = ({ label, icon, items, onLinkClick }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const { t } = useUiLanguage();

    useEffect(() => {
        const handleClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    if (items.length === 0) return null;

    return (
        <div className="nav-dropdown" ref={ref}>
            <button
                className={`nav-dropdown-trigger text-foreground hover:bg-muted ${open ? 'is-open' : ''}`}
                onClick={() => setOpen(!open)}
            >
                <span className="nav-dropdown-icon">{icon}</span>
                {label}
                <span className="nav-dropdown-arrow text-muted-foreground">{open ? '▴' : '▾'}</span>
            </button>
            {open && (
                <div className="nav-dropdown-menu bg-popover border-border">
                    {items.map(item => (
                        <NavLink
                            key={item.path}
                            to={item.path}
                            onClick={() => { setOpen(false); onLinkClick(); }}
                            className={({ isActive }) => `nav-dropdown-item text-foreground hover:bg-accent-soft ${isActive ? 'active nav-item--active' : ''}`}
                        >
                            <span className="nav-dropdown-item-icon">{item.icon}</span>
                            {t(`shell.dest.${item.key}`, item.label)}
                        </NavLink>
                    ))}
                </div>
            )}
        </div>
    );
};

const Navbar = () => {
    const { user, navLinks, logout, isAuthenticated, loading } = useAuth();
    const { branding, isFitenko } = useBranding();
    const { t } = useUiLanguage();
    const counters = useNavCounters();
    const navigate = useNavigate();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const brandName = branding?.instance_name ? `${branding.instance_name.toUpperCase()} OS` : '';

    const handleLogout = () => { logout(); setIsMenuOpen(false); };
    const handleLinkClick = () => { setIsMenuOpen(false); };

    if (loading || !isAuthenticated) return null;

    const userDisplayName = user?.full_name || user?.first_name || user?.email || 'User';
    const userRole = getUserRoleLabel(user?.roles);
    const roleColors = getRoleColor(userRole);

    const groups = legacyGroups(user, navLinks);
    const businessItems = groups.business;
    const aiItems = groups.ai;
    const seoItems = groups.seo;
    const toolsItems = groups.tools;
    const adminItems = groups.admin;

    const rail = isFitenko ? fitenkoRail(user, navLinks) : null;

    return (
        <nav className="main-navbar bg-card text-foreground border-b border-border">
            <NavLink to="/dashboard" className="navbar-brand text-primary" onClick={handleLinkClick}>
                {isFitenko ? <FitenkoRailHead tenant={branding?.company_name} /> : brandName}
            </NavLink>

            <button
                className={`menu-toggle ${isMenuOpen ? 'is-open' : ''}`}
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-label="Toggle menu"
                aria-expanded={isMenuOpen}
            >
                <div className="hamburger-line"></div>
                <div className="hamburger-line"></div>
                <div className="hamburger-line"></div>
            </button>

            <div className={`navbar-links bg-card ${isMenuOpen ? 'is-open' : ''}`}>
                {rail ? (
                    <FitenkoRail sections={rail} counters={counters} onLinkClick={handleLinkClick} />
                ) : (
                    <>
                        <NavLink
                            to="/dashboard"
                            onClick={handleLinkClick}
                            className={({ isActive }) => `text-foreground hover:bg-muted ${isActive ? 'active nav-item--active' : ''}`}
                        >
                            <span style={{ marginRight: '4px' }}>🏠</span> {t('shell.dest.dashboard', 'Dashboard')}
                        </NavLink>

                        <NavDropdown label={t('shell.group.business', 'Business')} icon="💼" items={businessItems} onLinkClick={handleLinkClick} />
                        <NavDropdown label={t('shell.group.ai', 'AI')} icon="🤖" items={aiItems} onLinkClick={handleLinkClick} />
                        <NavDropdown label={t('shell.group.growth', 'SEO')} icon="📈" items={seoItems} onLinkClick={handleLinkClick} />
                        {toolsItems.length > 0 && <NavDropdown label={t('shell.group.tools', 'Tools')} icon="🛠️" items={toolsItems} onLinkClick={handleLinkClick} />}
                        {adminItems.length > 0 && <NavDropdown label={t('shell.group.admin', 'Admin')} icon="⚙️" items={adminItems} onLinkClick={handleLinkClick} />}
                    </>
                )}
            </div>

            {isFitenko && <FitenkoVerhnyayaPanel />}

            {isFitenko && canSeeKatifi(user) && (
                <button
                    type="button"
                    className="fk-topbar-ask"
                    onClick={() => window.dispatchEvent(new CustomEvent('katifi:open'))}
                >
                    {t('shell.ask_ai', { name: aiName(user) })}
                </button>
            )}

            <div className={`navbar-user-section text-muted-foreground${isFitenko ? ' fk-rail-user' : ''}`}>
                <UiLanguageToggle />
                <NavLink
                    to="/profile"
                    className="user-profile-link text-foreground"
                    onClick={handleLinkClick}
                    style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none' }}
                >
                    {isFitenko ? (
                        <FitenkoRailUser name={userDisplayName} role={userRole} />
                    ) : (
                        <>
                        <span style={{ fontWeight: '600' }}>{userDisplayName}</span>
                        {userRole && (
                        <span style={{
                            fontSize: '0.7rem',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            backgroundColor: roleColors.bg,
                            color: roleColors.text,
                            border: `1px solid ${roleColors.border}`,
                            fontWeight: '700',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            lineHeight: '1'
                        }}>
                            {userRole}
                        </span>
                        )}
                        </>
                    )}
                </NavLink>
                <button onClick={handleLogout} className="logout-button bg-muted hover:bg-primary hover:text-primary-foreground">{t('shell.auth.logout', 'Logout')}</button>
            </div>
        </nav>
    );
};

export default Navbar;
